import { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { SavedProposalDocument } from "@/components/proposal/SavedProposalDocument";
import AppLayout from "@/components/layout/AppLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, FileText, Download, Eye, Loader2, Calendar, MapPin, Share2, CheckCircle2, XCircle, Clock, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import RiskBadge from "@/components/proposal/RiskBadge";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { useStaffIndustryView } from "@/hooks/useStaffIndustryView";
import { UpgradeModal } from "@/components/upgrade/UpgradeModal";
import { EmptyState } from "@/components/ui/empty-state";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { PullToRefreshIndicator } from "@/components/ui/pull-to-refresh-indicator";

interface Proposal {
  id: string;
  address: string;
  property_type: string;
  officers: number;
  armed_status: string;
  days_of_coverage: number;
  hours_of_coverage: number;
  hourly_rate: number;
  notes: string | null;
  risk_level: string | null;
  executive_summary: string | null;
  threat_assessment: string | null;
  risk_impact: string | null;
  staffing_recommendation: string | null;
  security_actions: string | null;
  implementation_plan: string | null;
  expected_outcomes: string | null;
  crime_data_summary: string | null;
  source: string;
  created_at: string;
  // New multi-industry fields
  industry: string | null;
  client_name: string | null;
  client_company: string | null;
  client_email: string | null;
  total_cost: number | null;
  proposal_data: any | null;
}

interface SharedProposal {
  id: string;
  proposal_id: string;
  client_name: string | null;
  client_email: string;
  status: string;
  client_feedback: string | null;
  created_at: string;
  expires_at: string | null;
  responded_at: string | null;
}

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case "approved":
      return <CheckCircle2 className="h-4 w-4 text-primary" />;
    case "rejected":
      return <XCircle className="h-4 w-4 text-destructive" />;
    default:
      return <Clock className="h-4 w-4 text-muted-foreground" />;
  }
};

const statusLabel = (status: string) => {
  switch (status) {
    case "approved": return "Approved";
    case "rejected": return "Rejected";
    default: return "Pending";
  }
};

const statusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "approved": return "default";
    case "rejected": return "destructive";
    default: return "secondary";
  }
};

const ProposalTable = ({ loading, filtered, search, onClearSearch, onView, onExport, onShare, industry }: {
  loading: boolean;
  filtered: Proposal[];
  search: string;
  onClearSearch: () => void;
  onView: (p: Proposal) => void;
  onExport: (p: Proposal) => void;
  onShare?: (p: Proposal) => void;
  industry: string;
}) => {
  const isPI = industry === "private-investigation";
  const isCleaning = industry === "cleaning" || industry === "commercial-cleaning";

  // Industry-specific column labels
  const personnelLabel = isPI ? "Investigators" : isCleaning ? "—" : "Officers";
  const typeLabel = isPI ? "Case Type" : isCleaning ? "Facility" : "Property";
  const statusLabel = isPI ? "Urgency" : isCleaning ? "Frequency" : "Risk";

  const skeletonColumns = isCleaning
    ? [isPI ? "Location" : "Address", typeLabel, statusLabel, "Cost", "Date", "Actions"]
    : [isPI ? "Location" : "Address", typeLabel, statusLabel, personnelLabel, "Cost", "Date", "Actions"];

  return (
    <Card className="border-border bg-card shadow-card">
      <CardContent className="p-0">
        {loading ? (
          <TableSkeleton columns={skeletonColumns} rows={6} minWidth="650px" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={search ? "No matching proposals" : "No proposals yet"}
            description={
              search
                ? "Try a different search term or clear the filter to see all proposals."
                : "Create your first AI-generated proposal to see it here. It only takes a couple of minutes."
            }
            action={
              search ? (
                <Button variant="outline" onClick={onClearSearch}>Clear search</Button>
              ) : (
                <Button
                  onClick={() => (window.location.href = "/")}
                  className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
                >
                  <FileText className="mr-2 h-4 w-4" /> Create your first proposal
                </Button>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto -mx-6">
            <Table className="min-w-[650px]">
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="text-muted-foreground">{isPI ? "Location" : "Address"}</TableHead>
                  <TableHead className="text-muted-foreground">{typeLabel}</TableHead>
                  <TableHead className="text-muted-foreground">{statusLabel}</TableHead>
                  {!isCleaning && <TableHead className="text-muted-foreground">{personnelLabel}</TableHead>}
                  <TableHead className="text-muted-foreground">Cost</TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} className="border-border">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-foreground truncate max-w-[250px]">{p.address}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{p.property_type}</Badge>
                    </TableCell>
                    <TableCell>
                      {isPI ? (
                        <Badge variant="secondary" className="capitalize">{p.risk_level || "standard"}</Badge>
                      ) : isCleaning ? (
                        <Badge variant="secondary">{p.proposal_data?.cleaningFrequency || "—"}</Badge>
                      ) : (
                        p.risk_level && <RiskBadge level={p.risk_level as any} />
                      )}
                    </TableCell>
                    {!isCleaning && (
                      <TableCell className="text-foreground">
                        {isPI ? p.officers : `${p.officers} (${p.armed_status})`}
                      </TableCell>
                    )}
                    <TableCell className="text-foreground">
                      {p.total_cost ? `$${p.total_cost.toLocaleString()}` : `$${(p.officers * p.hourly_rate * p.hours_of_coverage * p.days_of_coverage).toLocaleString()}`}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(p.created_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onView(p)} title="View">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onExport(p)} title="Export PDF">
                          <Download className="h-4 w-4" />
                        </Button>
                        {onShare && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onShare(p)} title="Share to Client">
                            <Share2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SharedProposalsTable = ({ shares }: { shares: (SharedProposal & { proposal?: Proposal })[] }) => (
  <Card className="border-border bg-card shadow-card">
    <CardContent className="p-0">
      {shares.length === 0 ? (
        <EmptyState
          icon={Share2}
          title="No shared proposals yet"
          description='Share a proposal from the "My Proposals" tab and track approvals here.'
        />
      ) : (
        <div className="overflow-x-auto -mx-6">
          <Table className="min-w-[700px]">
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-muted-foreground">Proposal Address</TableHead>
                <TableHead className="text-muted-foreground">Client</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground">Sent</TableHead>
                <TableHead className="text-muted-foreground">Responded</TableHead>
                <TableHead className="text-muted-foreground">Feedback</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shares.map((s) => (
                <TableRow key={s.id} className="border-border">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-foreground truncate max-w-[250px]">
                        {s.proposal?.address ?? "Unknown"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      {s.client_name && <p className="text-foreground text-sm font-medium">{s.client_name}</p>}
                      <p className="text-muted-foreground text-xs">{s.client_email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(s.status)} className="capitalize gap-1">
                      <StatusIcon status={s.status} />
                      {statusLabel(s.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {new Date(s.created_at).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    {s.responded_at ? (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(s.responded_at).toLocaleDateString()}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {s.client_feedback ? (
                      <p className="text-xs text-muted-foreground truncate max-w-[200px]" title={s.client_feedback}>
                        {s.client_feedback}
                      </p>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardContent>
  </Card>
);

const ProposalHistory = () => {
  const { user, subscription } = useAuth();
  const { effectiveIndustry } = useStaffIndustryView();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [sharedProposals, setSharedProposals] = useState<(SharedProposal & { proposal?: Proposal })[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Proposal | null>(null);
  const [shareEmail, setShareEmail] = useState("");
  const [shareName, setShareName] = useState("");
  const [sharing, setSharing] = useState(false);
  const [showShareForm, setShowShareForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Proposal>>({});
  const [saving, setSaving] = useState(false);
  const [showShareUpgrade, setShowShareUpgrade] = useState(false);

  const isAgencyOrAbove = subscription.tier === "agency" || subscription.tier === "enterprise" || subscription.tier === "testing";

  useEffect(() => {
    if (user) {
      fetchProposals();
      if (isAgencyOrAbove) fetchSharedProposals();
    }
  }, [user, isAgencyOrAbove, effectiveIndustry]);

  const { pullDistance, refreshing, threshold } = usePullToRefresh({
    onRefresh: async () => {
      await fetchProposals();
      if (isAgencyOrAbove) await fetchSharedProposals();
    },
  });

  const fetchProposals = async () => {
    setLoading(true);
    
    // Map effectiveIndustry to DB values
    const industryFilter = effectiveIndustry === "private-investigation" 
      ? "private-investigation"
      : effectiveIndustry === "cleaning" || effectiveIndustry === "commercial-cleaning"
        ? "cleaning"
        : effectiveIndustry === "event-staffing"
          ? "event-staffing"
          : effectiveIndustry || "security";
    
    let query = supabase
      .from("proposals")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);

    
    // Filter by industry (older proposals without industry field are treated as security)
    if (industryFilter === "security") {
      query = query.or(`industry.eq.security,industry.is.null`);
    } else {
      query = query.eq("industry", industryFilter);
    }
    
    const { data, error } = await query;
    if (error) toast.error(error.message);
    else setProposals((data ?? []) as Proposal[]);
    setLoading(false);
  };

  const fetchSharedProposals = async () => {
    const { data: shares, error } = await supabase
      .from("client_portal_shares")
      .select("id, proposal_id, client_name, client_email, status, client_feedback, created_at, expires_at, responded_at")
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) {
      console.error("Failed to fetch shared proposals:", error);
      return;
    }
    // Enrich with proposal data
    const proposalIds = [...new Set((shares ?? []).map((s: any) => s.proposal_id))];
    if (proposalIds.length === 0) {
      setSharedProposals([]);
      return;
    }
    const { data: proposalData } = await supabase
      .from("proposals")
      .select("*")
      .in("id", proposalIds);
    const proposalMap = new Map((proposalData ?? []).map((p: any) => [p.id, p]));
    setSharedProposals(
      (shares ?? []).map((s: any) => ({
        ...s,
        proposal: proposalMap.get(s.proposal_id),
      }))
    );
  };

  const filtered = proposals.filter((p) => {
    const q = search.toLowerCase();
    return !q || p.address.toLowerCase().includes(q) || p.property_type.toLowerCase().includes(q) || p.risk_level?.toLowerCase().includes(q);
  });

  const exportPDF = async (p: Proposal) => {
    toast.info("Preparing PDF...");
    const host = document.createElement("div");
    host.id = `saved-proposal-export-${p.id}`;
    host.className = "proposal-export-host";
    document.body.appendChild(host);
    const root = createRoot(host);
    try {
      flushSync(() => root.render(<SavedProposalDocument proposal={p} />));
      const { exportProposalPDF } = await import("@/lib/exportProposalPDF");
      await exportProposalPDF({
        elementId: host.id,
        filenamePrefix: `proposal-${p.id.substring(0, 8)}`,
        industryLabel: `${(p.industry || "security").replace(/-/g, " ")} Proposal`,
        ...(p.proposal_data?.branding ?? { companyName: p.client_company || undefined }),
      });
      toast.success("PDF exported");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      root.unmount();
      host.remove();
    }
  };

  return (
    <AppLayout>
      <PullToRefreshIndicator pullDistance={pullDistance} refreshing={refreshing} threshold={threshold} />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {effectiveIndustry === "private-investigation" ? "Investigation" 
                : effectiveIndustry === "cleaning" || effectiveIndustry === "commercial-cleaning" ? "Cleaning"
                : effectiveIndustry === "event-staffing" ? "Staffing"
                : "Security"} Proposal History
            </h1>
            <p className="text-muted-foreground mt-1">{proposals.length} proposals generated</p>
          </div>
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by address, type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-secondary border-border text-foreground"
            />
          </div>
        </div>

        {isAgencyOrAbove ? (
          <Tabs defaultValue="proposals" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="proposals">My Proposals</TabsTrigger>
              <TabsTrigger value="shared">
                Shared to Clients
                {sharedProposals.length > 0 && (
                  <Badge variant="secondary" className="ml-2 text-xs">{sharedProposals.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="proposals">
              <ProposalTable loading={loading} filtered={filtered} search={search} onClearSearch={() => setSearch("")} onView={setSelected} onExport={exportPDF} onShare={(p) => { setSelected(p); setShowShareForm(true); }} industry={effectiveIndustry} />
            </TabsContent>
            <TabsContent value="shared">
              <SharedProposalsTable shares={sharedProposals} />
            </TabsContent>
          </Tabs>
        ) : (
          <ProposalTable loading={loading} filtered={filtered} search={search} onClearSearch={() => setSearch("")} onView={setSelected} onExport={exportPDF} onShare={() => setShowShareUpgrade(true)} industry={effectiveIndustry} />
        )}
      </div>

      <UpgradeModal
        open={showShareUpgrade}
        onOpenChange={setShowShareUpgrade}
        feature="client-portal"
      />

      {/* Proposal Detail Dialog */}
      <Dialog open={!!selected && !showShareForm} onOpenChange={(open) => { if (!open) { setSelected(null); setEditing(false); setEditData({}); } }}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-foreground flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-primary" />
                  {selected.address}
                </DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Badge variant="outline" className="capitalize">{selected.property_type}</Badge>
                {selected.risk_level && <RiskBadge level={selected.risk_level as any} />}
                <span className="text-xs text-muted-foreground">
                  {selected.officers} {selected.armed_status} officer(s) · ${selected.hourly_rate}/hr · {selected.days_of_coverage}d/wk · {selected.hours_of_coverage}h/day
                </span>
                <div className="flex gap-1 ml-auto">
                  {!editing ? (
                    <Button variant="outline" size="sm" onClick={() => {
                      setEditing(true);
                      setEditData({
                        executive_summary: selected.executive_summary,
                        threat_assessment: selected.threat_assessment,
                        risk_impact: selected.risk_impact,
                        staffing_recommendation: selected.staffing_recommendation,
                        security_actions: selected.security_actions,
                        crime_data_summary: selected.crime_data_summary,
                        implementation_plan: selected.implementation_plan,
                        expected_outcomes: selected.expected_outcomes,
                        notes: selected.notes,
                      });
                    }}>
                      <Pencil className="h-4 w-4 mr-1" /> Edit
                    </Button>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditData({}); }}>
                        <X className="h-4 w-4 mr-1" /> Cancel
                      </Button>
                      <Button size="sm" className="gradient-primary text-primary-foreground" disabled={saving} onClick={async () => {
                        setSaving(true);
                        try {
                          const { error } = await supabase
                            .from("proposals")
                            .update(editData)
                            .eq("id", selected.id);
                          if (error) throw error;
                          // Update local state
                          const updated = { ...selected, ...editData };
                          setSelected(updated);
                          setProposals(prev => prev.map(p => p.id === selected.id ? updated : p));
                          setEditing(false);
                          setEditData({});
                          toast.success("Proposal updated successfully");
                        } catch (err: any) {
                          toast.error(err.message || "Failed to save changes");
                        } finally {
                          setSaving(false);
                        }
                      }}>
                        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
                      </Button>
                    </>
                  )}
                  <Button variant="outline" size="sm" onClick={() => exportPDF(selected)}>
                    <Download className="h-4 w-4 mr-1" /> PDF
                  </Button>
                  {isAgencyOrAbove && (
                    <Button variant="outline" size="sm" onClick={() => setShowShareForm(true)}>
                      <Share2 className="h-4 w-4 mr-1" /> Share
                    </Button>
                  )}
                </div>
              </div>
              {!editing ? <SavedProposalDocument proposal={selected} /> : <div className="space-y-4">
                {[
                  { title: "Executive Summary", key: "executive_summary" as keyof Proposal },
                  { title: "Threat Assessment", key: "threat_assessment" as keyof Proposal },
                  { title: "Risk & Impact", key: "risk_impact" as keyof Proposal },
                  { title: "Staffing Recommendation", key: "staffing_recommendation" as keyof Proposal },
                  { title: "Security Actions", key: "security_actions" as keyof Proposal },
                  { title: "Crime Data", key: "crime_data_summary" as keyof Proposal },
                  { title: "Implementation Plan", key: "implementation_plan" as keyof Proposal },
                  { title: "Expected Outcomes", key: "expected_outcomes" as keyof Proposal },
                ].filter(s => editing || selected[s.key]).map(({ title, key }) => (
                  <div key={key}>
                    <h4 className="text-sm font-semibold text-primary mb-1">{title}</h4>
                    {editing ? (
                      <Textarea
                        value={(editData[key] as string) ?? (selected[key] as string) ?? ""}
                        onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                        className="min-h-[100px] bg-secondary border-border text-foreground"
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selected[key] as string}</p>
                    )}
                  </div>
                ))}
              </div>}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Share to Client Dialog */}
      <Dialog open={showShareForm} onOpenChange={(open) => { if (!open) { setShowShareForm(false); setShareEmail(""); setShareName(""); } }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Share2 className="h-5 w-5 text-primary" />
              Share Proposal to Client Portal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Client Name (optional)</Label>
              <Input
                placeholder="John Smith"
                value={shareName}
                onChange={(e) => setShareName(e.target.value)}
                className="mt-1 bg-secondary border-border"
              />
            </div>
            <div>
              <Label className="text-muted-foreground">Client Email</Label>
              <Input
                type="email"
                placeholder="client@example.com"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                className="mt-1 bg-secondary border-border"
              />
            </div>
            <Button
              className="w-full gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
              disabled={sharing || !shareEmail}
              onClick={async () => {
                if (!selected || !shareEmail) return;
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(shareEmail)) {
                  toast.error("Please enter a valid email");
                  return;
                }
                setSharing(true);
                try {
                  const { data, error } = await supabase.functions.invoke("client-portal", {
                    body: {
                      action: "share-proposal",
                      proposalId: selected.id,
                      clientEmail: shareEmail,
                      clientName: shareName || undefined,
                    },
                  });
                  if (error) throw error;
                  if (data?.error) throw new Error(data.error);
                  toast.success(`Proposal shared with ${shareEmail}! They'll receive an email with the portal link.`);
                  setShowShareForm(false);
                  setShareEmail("");
                  setShareName("");
                  fetchSharedProposals();
                } catch (err: any) {
                  toast.error(err.message || "Failed to share proposal");
                } finally {
                  setSharing(false);
                }
              }}
            >
              {sharing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share2 className="mr-2 h-4 w-4" />}
              Send Portal Link
            </Button>
            <p className="text-xs text-muted-foreground">The client will receive an email with a secure link to view and approve/reject this proposal. The link expires in 30 days.</p>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default ProposalHistory;
